import { SummaryStateType } from '@components/SummarySelection/data/summaryState';
import { randomString } from '@lib/random';
import { Package, PackageType } from '@model/Package';
import { Sku, SkuType } from '@model/Sku';
import { baseFilter } from '@packages/functions/lib/baseFilter';
import { Record } from 'immutable';
import { find, get } from 'lodash';

export const selectCategory = (state: Record<SummaryStateType>): string => {
    const category_id = state.get('category_id');
    const categories = state.getIn(['rawData', 'categories', 'items']);

    if (categories) {
        const category = find(categories, ['id', category_id]);

        if (category) {
            return category.name;
        }
    }

    return '';
};

export interface ItemType {
    sku: any;
    selectable_sku: any;
    package: PackageType;
    item_specifications: any;
    id: number;
    type: typeof Package | typeof Sku;
    value: PackageType | SkuType;
}

export const selectItemsFromState = (state: Record<SummaryStateType>): ItemType[] => {
    const items: any = [];
    const packages = state.getIn(['data', 'packages', 'items']) || [];
    const skus = state.getIn(['data', 'skus', 'items']) || [];
    const packageId = state.get('package_id');
    const skusIds = (state.get('skus_ids') || []).filter((s) => s > 0);

    if (packageId) {
        items.push({
            id: packageId,
            type: Package,
            value: find(packages || [], ['id', packageId]),
        });
    }

    if (skusIds.length > 0) {
        skus.forEach((sku) => {
            if (skusIds.includes(sku.id)) {
                items.push({ id: sku.id, type: Sku, value: sku });
            }
        });
    }

    return items.filter(baseFilter);
};

const getItemType = (item) => {
    const package_id = (item.package || {}).id || item.package_id;
    const sku_id = (item.sku || {}).id || item.sku_id;
    const type$ = package_id ? 'package' : 'sku';

    return `${type$}_${type$ === 'package' ? package_id : sku_id}`;
};

const _flatCollection = (object) =>
    (object.item_specifications || []).reduce(
        (acc, item) => ({
            ...acc,
            ...item,
        }),
        {},
    );

export function prepareSummaryState(state: any = {}, order: any = {}) {
    const newState = Record.isRecord(state) ? state.toJS() : { ...state };

    const items$ = get(order, 'items') || [];
    const quantities = items$.reduce(
        (obj, item) => ({
            ...obj,
            [getItemType(item)]:
                (item.hasOwnProperty('quantity') ? item.quantity : _flatCollection(item).quantity) || 1,
        }),
        {},
    );

    const filtered = (newState.items || []).filter((item) => !item.package);

    if (newState.package_id && newState.package) {
        const subCats = newState.package.subcategories || [];

        const packageQt = quantities[getItemType(newState)];

        newState.items = [
            ...filtered,
            ...subCats
                .filter(({ default_sku }) => default_sku)
                .reduce((acc, { default_sku }) => {
                    const quantity = packageQt ? packageQt : newState.package.quantity ? newState.package.quantity : 1;

                    return [
                        ...acc,
                        {
                            id: randomString(),
                            package: newState.package,
                            sku: default_sku,
                            sku_id: default_sku.id,
                            item_specifications: [{ quantity }],
                            quantity,
                        },
                    ];
                }, []),
        ];
    } else {
        newState.items = [...filtered];
    }

    newState.items = [
        ...newState.items,
        ...(newState.skus || []).map((sku) => {
            const quantity = quantities[getItemType({ sku })] || (sku.quantity ? sku.quantity : 1);

            return {
                id: randomString(),
                sku: sku,
                sku_id: sku.id,
                item_specifications: [{ quantity }],
                quantity,
            };
        }),
    ];

    return newState;
}

const getItemKey = (item) =>
    `${(item.package || {}).id || item.package_id || 'null'}_${(item.sku || {}).id || item.sku_id || 'null'}`;

export const mapSelectionItems = (state = {}, order = {}, itemsProp = 'items') => {
    const items$ = prepareSummaryState(state, order).items.map((item) => {
        const specs = _flatCollection(item);
        return {
            id: item.id,
            package_id: item.package ? item.package.id : null,
            sku_id: item.sku ? item.sku.id : null,
            package: item.package,
            sku: item.sku,
            quantity: item.quantity || specs.quantity,
        };
    });

    const itemsMeta = (order[itemsProp] || []).reduce((acc, { id, quantity, ...item }) => {
        const qt = _flatCollection(item).quantity;

        return {
            ...acc,
            [getItemKey(item)]: { id, quantity: quantity !== qt ? qt : quantity },
        };
    }, {});

    return items$.map((item) => {
        const key = getItemKey(item);

        item.id = itemsMeta.hasOwnProperty(key) ? itemsMeta[key].id : item.id || randomString();
        item.quantity = itemsMeta.hasOwnProperty(key) ? itemsMeta[key].quantity : item.quantity;

        return item;
    });
};
